import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap, AdminEmptyState, slugify } from "@/components/admin/crud-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ImportDefaultsButton } from "@/components/admin/import-defaults-button";
import { buildCategorySeeds } from "@/lib/admin-seeds";

export const Route = createFileRoute("/_authenticated/admin/categories")({
  component: CategoriesAdmin,
});

type Category = {
  id: string; name: string; slug: string; description: string | null;
  parent_id: string | null; sort_order: number;
};

function CategoriesAdmin() {
  const { data, isLoading } = useResourceList<Category>("categories", "name", true);
  const { create, update, remove } = useResourceMutations("categories");
  const [editing, setEditing] = useState<Category | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const byParent = useMemo(() => {
    const m = new Map<string | null, Category[]>();
    (data ?? []).forEach((c) => {
      const k = c.parent_id ?? null;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(c);
    });
    return m;
  }, [data]);

  const allRows: { c: Category; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    (byParent.get(parent) ?? []).forEach((c) => {
      allRows.push({ c, depth });
      walk(c.id, depth + 1);
    });
  };
  walk(null, 0);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(({ c }) =>
      c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q) || (c.description ?? "").toLowerCase().includes(q),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, search]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Categories"
        description="Shared categories with optional parent for sub & child categories. Used by posts, products and the public /blog/category and /marketplace/category pages."
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New category"
        actions={<ImportDefaultsButton table="categories" onConflict="slug" loadRows={buildCategorySeeds} />}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search categories…"
        count={rows.length}
      />
      {!isLoading && !rows.length ? (
        <AdminEmptyState title={search ? "No categories match" : "No categories yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Slug</TableHead>
                  <TableHead className="hidden sm:table-cell">Level</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
                {rows.map(({ c, depth }) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      <span style={{ paddingLeft: depth * 16 }}>{depth > 0 ? "↳ " : ""}{c.name}</span>
                      <div className="mt-0.5 text-xs text-muted-foreground sm:hidden">{c.slug}</div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">{c.slug}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="secondary">{depth === 0 ? "Root" : depth === 1 ? "Sub" : `L${depth}`}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions onEdit={() => { setEditing(c); setOpen(true); }} onDelete={() => { if (confirm(`Delete "${c.name}"?`)) remove.mutate(c.id); }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}
      <CategoryDialog
        key={editing?.id ?? "new"}
        open={open} onOpenChange={setOpen} editing={editing}
        all={data ?? []}
        onSubmit={(v: any) => {
          if (editing) update.mutate({ id: editing.id, values: v }, { onSuccess: () => setOpen(false) });
          else create.mutate(v, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function descendantIds(all: Category[], id: string): Set<string> {
  const out = new Set<string>([id]);
  let added = true;
  while (added) {
    added = false;
    for (const c of all) {
      if (c.parent_id && out.has(c.parent_id) && !out.has(c.id)) {
        out.add(c.id);
        added = true;
      }
    }
  }
  return out;
}

function CategoryDialog({ open, onOpenChange, editing, all, onSubmit }: any) {
  const [name, setName] = useState(editing?.name ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [desc, setDesc] = useState(editing?.description ?? "");
  const [parentId, setParentId] = useState<string>(editing?.parent_id ?? "none");
  const [sortOrder, setSortOrder] = useState<number>(editing?.sort_order ?? 0);

  const blocked = editing ? descendantIds(all, editing.id) : new Set<string>();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="grid gap-2"><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={slugify(name)} /></div>
          <div className="grid gap-2">
            <Label>Parent category</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger><SelectValue placeholder="None (root)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None (root) —</SelectItem>
                {(all as Category[]).filter((c) => !blocked.has(c.id)).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2"><Label>Sort order</Label><Input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} /></div>
          <div className="grid gap-2"><Label>Description</Label><Textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({
            name, slug: slug || slugify(name),
            description: desc || null,
            parent_id: parentId === "none" ? null : parentId,
            sort_order: sortOrder,
          })}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
